import React, { useState, useEffect } from 'react';
import { Clock, Cloud, CloudRain, Sun, CloudSnow, Wind } from 'lucide-react';

interface WeatherData {
  temperature: number;
  condition: string;
  icon: string;
}

export function TimeWeatherWidget() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch weather on mount
  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    setIsLoadingWeather(true);
    try {
      // Get user's location
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;

            // Use Open-Meteo API (free, no API key required)
            const response = await fetch(
              `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&timezone=auto`
            );

            if (response.ok) {
              const data = await response.json();
              const weatherCode = data.current.weather_code;

              setWeather({
                temperature: Math.round(data.current.temperature_2m),
                condition: getWeatherCondition(weatherCode),
                icon: getWeatherIcon(weatherCode),
              });
            }
          },
          (error) => {
            console.warn('Could not get location for weather:', error);
          }
        );
      }
    } catch (error) {
      console.error('Error fetching weather:', error);
    } finally {
      setIsLoadingWeather(false);
    }
  };

  const getWeatherCondition = (code: number): string => {
    if (code === 0) return 'Clear';
    if (code <= 3) return 'Partly Cloudy';
    if (code <= 48) return 'Foggy';
    if (code <= 67) return 'Rainy';
    if (code <= 77) return 'Snowy';
    if (code <= 82) return 'Showers';
    if (code <= 86) return 'Snow Showers';
    return 'Stormy';
  };

  const getWeatherIcon = (code: number): string => {
    if (code === 0) return 'sun';
    if (code <= 3) return 'cloud';
    if (code <= 48) return 'cloud';
    if (code <= 67) return 'rain';
    if (code <= 77) return 'snow';
    if (code <= 82) return 'rain';
    if (code <= 86) return 'snow';
    return 'wind';
  };

  const WeatherIcon = ({ icon }: { icon: string }) => {
    switch (icon) {
      case 'sun':
        return <Sun className="w-4 h-4 text-amber-500" />;
      case 'rain':
        return <CloudRain className="w-4 h-4 text-blue-500" />;
      case 'snow':
        return <CloudSnow className="w-4 h-4 text-blue-400" />;
      case 'wind':
        return <Wind className="w-4 h-4 text-slate-500" />;
      case 'cloud':
      default:
        return <Cloud className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="flex items-center space-x-4 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
      {/* Time Display */}
      <div className="flex items-center space-x-2">
        <Clock className="w-4 h-4 text-slate-600" />
        <div className="text-right">
          <div className="text-sm font-semibold text-slate-800 tabular-nums">
            {formatTime(currentTime)}
          </div>
          <div className="text-xs text-slate-500">
            {formatDate(currentTime)}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="h-8 w-px bg-slate-300" />

      {/* Weather Display */}
      {weather ? (
        <div className="flex items-center space-x-2">
          <WeatherIcon icon={weather.icon} />
          <div className="text-right">
            <div className="text-sm font-semibold text-slate-800">
              {weather.temperature}°C
            </div>
            <div className="text-xs text-slate-500">
              {weather.condition}
            </div>
          </div>
        </div>
      ) : isLoadingWeather ? (
        <div className="flex items-center space-x-2 text-xs text-slate-500">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-slate-400"></div>
          <span>Loading...</span>
        </div>
      ) : (
        <button
          onClick={fetchWeather}
          className="text-xs text-slate-500 hover:text-slate-700 transition-colors duration-200"
        >
          Get Weather
        </button>
      )}
    </div>
  );
}
